import { useEffect, useState } from 'react'

import { type FeatureFlagKeys, useFeatureFlag } from './useFeatureFlag'

export const FEATURE_FLAG_WAIT_MS = 3_000

export const resolveAwaitedFlag = (value: unknown, hasWaited: boolean): boolean | null => {
  if (value === true) {
    return true
  }
  return hasWaited ? false : null
}

// A cold load reads the flag before PostHog has fetched it, so "not loaded yet" looks like "off".
// Waiting a short, bounded time lets a flag-on user through without stranding anyone when PostHog
// never loads (feature-flags.md: flagged-surfaces-wait-for-the-flag).
export const useAwaitedFeatureFlag = (
  flag: FeatureFlagKeys,
  waitMs: number = FEATURE_FLAG_WAIT_MS
): boolean | null => {
  const { value } = useFeatureFlag(flag, { defaultValue: false })
  const [hasWaited, setHasWaited] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setHasWaited(true), waitMs)
    return () => window.clearTimeout(timer)
  }, [waitMs])
  return resolveAwaitedFlag(value, hasWaited)
}

export const resolveSettledFlag = (value: unknown, hasWaited: boolean): boolean | null => {
  if (typeof value === 'boolean') {
    return value
  }
  return hasWaited ? false : null
}

// Unlike useAwaitedFeatureFlag, an explicit `false` from PostHog settles at once, so a flag at 0%
// never holds a screen for the whole wait; only an unresolved flag waits for the bound.
export const useSettledFeatureFlag = (
  flag: FeatureFlagKeys,
  waitMs: number = FEATURE_FLAG_WAIT_MS
): boolean | null => {
  const { value } = useFeatureFlag(flag, { defaultValue: null })
  const [hasWaited, setHasWaited] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setHasWaited(true), waitMs)
    return () => window.clearTimeout(timer)
  }, [waitMs])
  return resolveSettledFlag(value, hasWaited)
}
